import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useInView } from "react-intersection-observer";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AnimatedText from "../AnimatedText";
import { useSlideOffset } from "../../hooks/useSlideOffset";

const About: React.FC = () => {
  const navigate = useNavigate();
  // Full-width on mobile: sliding sideways would clip against the section edge.
  const columnSlide = useSlideOffset(50);
  const cardSlide = useSlideOffset(20);
  const [inViewRef] = useInView({ threshold: 0.3, triggerOnce: false });

  const aboutSectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: aboutSectionRef,
    offset: ["start end", "end start"],
  });

  const progress = useTransform(scrollYProgress, [0, 1], [0, 1]);

  const yBack = useTransform(progress, [0, 0.4, 0.7, 1], [400, 0, -50, -300]);
  const yMiddle = useTransform(progress, [0, 0.35, 0.65, 1], [500, 0, -80, -450]);
  const yFront = useTransform(progress, [0, 0.45, 0.75, 1], [300, 0, -30, -200]);

  const scaleBack = useTransform(progress, [0, 0.4], [1.15, 1]);
  const scaleMiddle = useTransform(progress, [0, 0.35], [1.2, 1]);

  const opacityAll = useTransform(progress, [0, 0.3, 0.7], [0, 1, 0.9]);

  // Description column drifts down, image column drifts up.
  const aboutDescriptionY = useTransform(progress, [0, 1], [-50, 90]);
  const aboutImagesColumnY = useTransform(progress, [0, 1], [60, -90]);

  return (
    <motion.section
      id="about"
      ref={aboutSectionRef}
      className="relative min-h-screen bg-white dark:bg-[#121212] py-40 px-6 md:px-12 lg:px-24 overflow-hidden flex flex-col-reverse lg:flex-row-reverse items-center justify-between"
    >
      <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-white via-white/70 to-transparent dark:from-[#121212] dark:via-[#121212]/70 pointer-events-none z-30" />
      <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-white via-white/70 to-transparent dark:from-[#121212] dark:via-[#121212]/70 pointer-events-none z-30" />

      <motion.div
        className="w-full lg:w-1/2 text-center lg:text-left flex flex-col items-center lg:items-start z-40 mb-12 lg:mb-0 max-w-2xl mx-auto lg:mx-0 lg:ml-auto"
        initial={{ opacity: 0, x: columnSlide }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 0.2 }}
        viewport={{ once: false }}
        style={{ y: aboutDescriptionY }}
      >
        <p className="text-[10px] uppercase font-black text-[#172b81] dark:text-[#a8c0ff] mb-4 tracking-[0.5em]">
          OUR STORY
        </p>

        <AnimatedText
          as={motion.h2}
          className="text-4xl sm:text-6xl font-black mb-6 text-slate-900 dark:text-white leading-tight tracking-tighter"
          segments={[
            { text: "Driving Philippine" },
            { text: "Cold Chain", className: "font-light italic text-slate-300 dark:text-slate-500" },
            { text: "Excellence" },
          ]}
        />

        <p className="text-lg mb-8 text-slate-500 dark:text-slate-400 font-light leading-relaxed lg:text-justify">
          RK & K is a proudly Filipino-owned provider of high-quality freezers and chillers tailored for industrial, medical, and household needs.
        </p>

        <div className="space-y-4 mb-10 w-full">
          {[
            {
              title: "National Reach & Inventory",
              text: "Extensive stock and robust logistics ensure timely delivery anywhere in the Philippines.",
              icon: <LayersOutlinedIcon />
            },
            {
              title: "Quality and Warranty Assurance",
              text: "We stand by our products with comprehensive warranties and dedicated support.",
              icon: <AutoAwesomeOutlinedIcon />
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              className="flex items-start justify-center lg:justify-start text-left bg-slate-50 dark:bg-white/[0.03] p-6 rounded-[2rem] border border-slate-100 dark:border-white/5"
              initial={{ opacity: 0, x: cardSlide }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 + (i * 0.2) }}
              viewport={{ once: false }}
            >
              <div className="text-[#172b81] dark:text-[#a8c0ff] mr-4 mt-1 flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-tight text-sm">{item.title}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-light">{item.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <button
          className="group relative flex items-center justify-center px-10 py-4 bg-[#172b81] text-white rounded-full overflow-hidden transition-all hover:scale-105 active:scale-95 shadow-xl shadow-blue-900/20 cursor-pointer"
          onClick={() => navigate('/about')}
        >
          <span className="relative z-10 font-bold uppercase tracking-widest text-[10px]">Read Full Story</span>
          <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
        </button>
      </motion.div>

      <motion.div
        className="relative w-full lg:w-1/2 flex items-center justify-center min-h-[550px] lg:min-h-[650px] lg:mt-0 overflow-hidden lg:overflow-visible"
        style={{ y: aboutImagesColumnY }}
      >
        <div ref={inViewRef} className="relative w-full h-full flex items-center justify-center">
          <motion.div
            className="relative w-[350px] h-[450px] md:w-[380px] md:h-[500px]"
            style={{ perspective: 1200 }}
          >
            <motion.img
              src="/images/artisanstackquad.webp"
              alt="Artisan Stack Quad"
              loading="lazy"
              decoding="async"
              className="absolute object-contain"
              style={{
                top: "-60%",
                left: "-40%",
                width: "85%",
                zIndex: 8,
                rotate: "-20deg",
                y: yBack,
                scale: scaleBack,
                opacity: opacityAll,
                willChange: "transform, opacity",
              }}
            />

            <motion.img
              src="/images/efe3002.webp"
              alt="EFE 3002"
              loading="lazy"
              decoding="async"
              className="absolute object-contain drop-shadow-[0_25px_25px_rgba(0,0,0,0.15)]"
              style={{
                top: "35%",
                left: "30%",
                width: "100%",
                zIndex: 20,
                rotate: "15deg",
                y: yMiddle,
                scale: scaleMiddle,
                willChange: "transform",
              }}
            />

            <motion.img
              src="/images/artisansingledoor.webp"
              alt="Artisan Single Door"
              loading="lazy"
              decoding="async"
              className="absolute object-contain drop-shadow-[0_35px_35px_rgba(0,0,0,0.2)]"
              style={{
                top: "80%",
                left: "-35%",
                width: "66%",
                zIndex: 30,
                rotate: "5deg",
                y: yFront,
                filter: "brightness(1.05)",
                opacity: 1,
                willChange: "transform",
              }}
            />
          </motion.div>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default About;
